const URL_REGEX = /(https?:\/\/[^\s]+)/gi;
const DOMAIN_REGEX = /\b([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}\b/gi;
const EMAIL_REGEX = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/gi;

export const detectAndReplaceLinks = (editor) => {
  const body = editor.getBody();
  if (!body) return;

  const selection = editor.selection;
  const range = selection.getRng();

  // Check if we're at the end of an anchor tag with two whitespaces
  if (range.endContainer.parentElement && range.endContainer.parentElement.tagName === 'A') {
    const textNode = range.endContainer;
    const text = textNode.textContent;
    // Clean invisible characters and convert non-breaking spaces to regular spaces
    const cleanText = text.replace(/\uFEFF/g, '').replace(/\u00A0/g, ' ');

    const cursorPosition = range.endOffset;

    // Check if there are two or more whitespaces at the end of the anchor tag
    const hasMultipleSpaces = / +$/.test(cleanText);
    const trailingSpacesMatch = cleanText.match(/ +$/);
    const trailingSpacesLength = trailingSpacesMatch ? trailingSpacesMatch[0].length : 0;
    const nonSpaceLength = cleanText.length - trailingSpacesLength;

    if (hasMultipleSpaces && cursorPosition >= nonSpaceLength) {
      // Remove the trailing spaces from the anchor tag
      textNode.textContent = cleanText.slice(0, nonSpaceLength);

      // Use TinyMCE's API to insert content after the anchor
      const anchorElement = range.endContainer.parentElement;

      // Move cursor after the anchor element
      const newRange = document.createRange();
      newRange.setStartAfter(anchorElement);
      newRange.collapse(true);
      selection.setRng(newRange);

      // Insert a space using TinyMCE's insertContent
      editor.insertContent(' ');

      return;
    }
  }

  // Get the current text node where cursor is positioned
  let textNode = range.startContainer;
  if (textNode.nodeType !== Node.TEXT_NODE) {
    textNode = textNode.firstChild || textNode;
  }

  if (!textNode || textNode.nodeType !== Node.TEXT_NODE) return;

  const parent = textNode.parentNode;
  // Skip if already inside a link
  if (parent.tagName === 'A') return;

  const text = textNode.textContent;
  const cursorPosition = range.startOffset;

  // Find the last phrase (word) before cursor position
  // Look for whitespace, newline, or start of text
  let phraseStart = cursorPosition;
  while (phraseStart > 0 && !/\s/.test(text[phraseStart - 1])) {
    phraseStart--;
  }

  // Check if we have a phrase to process (after whitespace or at start)
  if (phraseStart === 0 || /\s/.test(text[phraseStart - 1])) {
    // Find the end of the current phrase
    let phraseEnd = cursorPosition;
    while (phraseEnd < text.length && !/\s/.test(text[phraseEnd])) {
      phraseEnd++;
    }

    const currentPhrase = text.substring(phraseStart, phraseEnd);

    // Check if the current phrase matches our patterns
    const isUrl = URL_REGEX.test(currentPhrase);
    const isDomain = DOMAIN_REGEX.test(currentPhrase);
    const isEmail = EMAIL_REGEX.test(currentPhrase);

    if (isUrl || isDomain || isEmail) {
      let linkTag = '';
      if (isUrl) {
        linkTag = `<a href="${currentPhrase}" target="_blank" rel="noopener noreferrer">${currentPhrase}</a>`;
      } else if (isDomain) {
        linkTag = `<a href="https://${currentPhrase}" target="_blank" rel="noopener noreferrer">${currentPhrase}</a>`;
      } else if (isEmail) {
        linkTag = `<a href="mailto:${currentPhrase}">${currentPhrase}</a>`;
      }

      if (linkTag) {
        const beforePhrase = text.substring(0, phraseStart);
        const afterPhrase = text.substring(phraseEnd);
        const newText = beforePhrase + linkTag + afterPhrase;

        // Replace the text node with the new HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = newText;

        /*Store the position where we want the cursor (after the link)
        const linkElement = tempDiv.querySelector('a');
        const cursorTarget = linkElement ? linkElement.nextSibling : tempDiv.lastChild;*/

        while (tempDiv.firstChild) {
          parent.insertBefore(tempDiv.firstChild, textNode);
        }
        parent.removeChild(textNode);

        // Set expansion mode for the newly created link
        // Find the link we just created by looking for the one that matches our phrase
        const allLinks = parent.querySelectorAll('a');
        let newLinkElement = null;

        // Find the link that contains our current phrase
        for (let link of allLinks) {
          if (link.textContent === currentPhrase) {
            newLinkElement = link;
            break;
          }
        }

        if (newLinkElement) {
          // Store the link element for expansion
          window.currentExpandingLink = newLinkElement;
          window.isExpandingLink = true;

          // Place cursor INSIDE the link at the end without selecting
          const newRange = document.createRange();
          newRange.setStart(newLinkElement, 1); // 1 = after the text content
          newRange.collapse(true); // true = collapse to start position (no selection)
          selection.setRng(newRange);
        } else {
          // Fallback: place cursor at the end of the parent element
          const newRange = document.createRange();
          newRange.selectNodeContents(parent);
          newRange.collapse(false);
          selection.setRng(newRange);
        }
      }
    }
  }
};

export const createLinkDetectionHandler = (editor) => {
  const handleKeyDown = (e) => {
    // If user pressed whitespace, enter, or arrow right, exit link expansion
    if (window.isExpandingLink && (e.key === ' ' || e.key === 'Enter' || e.key === 'ArrowRight')) {
      window.isExpandingLink = false;
      window.currentExpandingLink = null;

      // If the link is selected, move cursor after it
      const selection = editor.selection;
      const range = selection.getRng();

      if (range.toString().trim() !== '') {
        // Link is selected, move cursor after the link
        const linkElement = window.currentExpandingLink;
        if (linkElement) {
          const newRange = document.createRange();
          newRange.setStartAfter(linkElement);
          newRange.collapse(true);
          selection.setRng(newRange);
        }
      }
    }
  };

  const expandCurrentLink = (editor, linkElement) => {
    const selection = editor.selection;
    const range = selection.getRng();

    // Get current text node
    let textNode = range.startContainer;
    if (textNode.nodeType !== Node.TEXT_NODE) {
      textNode = textNode.firstChild || textNode;
    }

    if (!textNode || textNode.nodeType !== Node.TEXT_NODE) return;

    const parent = textNode.parentNode;

    if (parent.tagName === 'A' && parent === linkElement) {
      // We're inside the link, update the href to match the current text content
      const currentText = linkElement.textContent;
      let newHref;

      if (currentText.match(URL_REGEX)) {
        newHref = currentText;
      } else if (currentText.match(DOMAIN_REGEX)) {
        newHref = 'https://' + currentText;
      } else if (currentText.match(EMAIL_REGEX)) {
        newHref = 'mailto:' + currentText;
      } else {
        // For partial domains, build the href
        newHref = 'https://' + currentText;
      }

      linkElement.href = newHref;
    } else {
      // We're after the link, move the text into the link
      const text = textNode.textContent;
      const cursorPosition = range.startOffset;
      const textAfterCursor = text.substring(cursorPosition);

      if (textAfterCursor) {
        // Move the text into the link
        linkElement.textContent = linkElement.textContent + textAfterCursor;

        // Update href based on the new content
        const newText = linkElement.textContent;
        let newHref;

        if (newText.match(URL_REGEX)) {
          newHref = newText;
        } else if (newText.match(DOMAIN_REGEX)) {
          newHref = 'https://' + newText;
        } else if (newText.match(EMAIL_REGEX)) {
          newHref = 'mailto:' + newText;
        } else {
          newHref = 'https://' + newText;
        }

        linkElement.href = newHref;

        // Remove the text from the original position
        textNode.textContent = text.substring(0, cursorPosition);

        // Place cursor at the end of the link without selecting
        const newRange = document.createRange();
        newRange.setStart(linkElement, 1);
        newRange.collapse(true);
        selection.setRng(newRange);
      }
    }
  };

  return { handleKeyDown, expandCurrentLink };
};
